import React from "react";
import { Handle, Position } from "reactflow";

interface StaticPipelineNodeProps {
  data: {
    label: string;
    processorType: string;
    status: string;
    isSubAgent: boolean;
    isWorkflowPaused?: boolean;
  };
}

// Map processor names to their types (matching backend categories)
const getProcessorTypeCategory = (
  processorName: string
): "SCRIPTED" | "AGENTIC" | "UTILITY" | "ORCHESTRATOR" | "VECTOR" => {
  switch (processorName) {
    case "orchestrator":
      return "ORCHESTRATOR";
    case "agentic_task":
      return "AGENTIC";
    case "vector":
      return "VECTOR";
    case "workspace":
    case "ticket":
      return "UTILITY";
    case "input":
    case "coding":
    case "task":
    case "pseudocode":
    default:
      return "SCRIPTED";
  }
};

const getProcessorColor = (
  processorType: string,
  isRunning: boolean = false,
  isPaused: boolean = false
) => {
  const category = getProcessorTypeCategory(processorType);

  if (isPaused) {
    // Amber/orange colors for paused state - same for all processor types
    return {
      bg: "#fb923c",
      border: "#f97316",
      text: "#7c2d12",
      glowColor: "rgba(249, 115, 22, 0.8)", // Amber glow
    };
  }

  if (isRunning) {
    // Bright glowing colors when running (lighter, more vibrant versions)
    switch (category) {
      case "SCRIPTED":
        return {
          bg: "#60a5fa",
          border: "#3b82f6",
          text: "#1e3a8a",
          glowColor: "rgba(59, 130, 246, 0.8)", // Blue glow
        };
      case "AGENTIC":
        return {
          bg: "#c084fc",
          border: "#a855f7",
          text: "#4c1d95",
          glowColor: "rgba(168, 85, 247, 0.8)", // Purple glow
        };
      case "UTILITY":
        return {
          bg: "#fbbf24",
          border: "#f59e0b",
          text: "#78350f",
          glowColor: "rgba(251, 191, 36, 0.8)", // Yellow glow
        };
      case "ORCHESTRATOR":
        return {
          bg: "#4ade80",
          border: "#22c55e",
          text: "#14532d",
          glowColor: "rgba(34, 197, 94, 0.8)", // Green glow
        };
      case "VECTOR":
        return {
          bg: "#5eead4",
          border: "#14b8a6",
          text: "#134e4a",
          glowColor: "rgba(20, 184, 166, 0.8)", // Teal glow
        };
      default:
        return {
          bg: "#94a3b8",
          border: "#64748b",
          text: "#1e293b",
          glowColor: "rgba(148, 163, 184, 0.8)", // Gray glow
        };
    }
  }

  // Darker colors when not running (matching pipeline builder badge colors)
  switch (category) {
    case "SCRIPTED":
      return {
        bg: "#1e3a8a",
        border: "#3b82f6",
        text: "#dbeafe",
        glowColor: "",
      };
    case "AGENTIC":
      return {
        bg: "#6b21a8",
        border: "#a855f7",
        text: "#f3e8ff",
        glowColor: "",
      };
    case "UTILITY":
      return {
        bg: "#854d0e",
        border: "#eab308",
        text: "#fef9c3",
        glowColor: "",
      };
    case "ORCHESTRATOR":
      return {
        bg: "#14532d",
        border: "#22c55e",
        text: "#dcfce7",
        glowColor: "",
      };
    case "VECTOR":
      return {
        bg: "#134e4a",
        border: "#14b8a6",
        text: "#ccfbf1",
        glowColor: "",
      };
    default:
      return {
        bg: "#475569",
        border: "#94a3b8",
        text: "#f1f5f9",
        glowColor: "",
      };
  }
};

const StaticPipelineNode: React.FC<StaticPipelineNodeProps> = ({ data }) => {
  const isRunning =
    data.status.toLowerCase() === "in_progress" ||
    data.status.toLowerCase() === "running" ||
    data.status.toLowerCase() === "calling_sub_agent";
  const isFailed =
    data.status.toLowerCase() === "failed" ||
    data.status.toLowerCase() === "error";

  // Distinguish between actively running vs paused at this step
  const isPaused = isRunning && data.isWorkflowPaused;
  const isActivelyRunning = isRunning && !data.isWorkflowPaused;

  const colors = getProcessorColor(
    data.processorType,
    isActivelyRunning,
    isPaused
  );

  return (
    <>
      <Handle
        id="left"
        type="target"
        position={Position.Left}
        style={{ background: colors.border }}
      />
      <div
        style={{
          background: colors.bg,
          border: `${isActivelyRunning || isPaused ? 3 : 2}px solid ${
            colors.border
          }`,
          borderRadius: "8px",
          padding: "12px 16px",
          width: "180px",
          height: "60px",
          color: colors.text,
          boxShadow: isActivelyRunning
            ? `0 0 20px ${
                colors.glowColor
              }, 0 0 40px ${colors.glowColor?.replace(
                "0.8",
                "0.4"
              )}, 0 4px 12px rgba(0,0,0,0.2)`
            : isPaused
            ? "0 4px 8px rgba(0,0,0,0.15)"
            : "0 2px 4px rgba(0,0,0,0.08)",
          animation: isActivelyRunning
            ? "dramaticPulse 1.5s ease-in-out infinite"
            : "none",
          transition: "all 0.3s ease",
          position: "relative",
          opacity: isFailed ? 0.65 : 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          transform: "scale(1)",
        }}
      >
        <div
          style={{
            fontSize: "14px",
            fontWeight: isActivelyRunning || isPaused ? 700 : 600,
            lineHeight: "1.4",
            textAlign: "center",
            overflow: "hidden",
            textOverflow: "ellipsis",
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            width: "100%",
          }}
          title={data.label}
        >
          {data.label}
        </div>

        {/* Status indicator - only show for failed */}
        {isFailed && (
          <div
            style={{
              position: "absolute",
              top: "8px",
              right: "8px",
              width: "10px",
              height: "10px",
              borderRadius: "50%",
              background: "#ef4444",
            }}
          />
        )}
      </div>
      <Handle
        id="right"
        type="source"
        position={Position.Right}
        style={{ background: colors.border }}
      />
      <Handle
        id="bottom"
        type="source"
        position={Position.Bottom}
        style={{ background: colors.border }}
      />
      <style>
        {`
          @keyframes dramaticPulse {
            0%, 100% {
              transform: scale(1.05);
              filter: brightness(1);
            }
            50% {
              transform: scale(1.08);
              filter: brightness(1.15);
            }
          }
        `}
      </style>
    </>
  );
};

// Temporarily removed memo to debug sub-agent rendering issue
export default StaticPipelineNode;
